import React from 'react';
import { Box, Stack, Text } from 'braid-design-system';

interface ReturnInvoiceItem {
  id: string;
  itemCode?: string;
  name: string;
  quantity: number;
  price: number;
  total: number;
  lineType?: 'SALE' | 'RETURN';
}

interface ReturnItemSelectionModalProps {
  isOpen: boolean;
  invoiceNo?: string;
  customerName?: string | null;
  items: ReturnInvoiceItem[];
  selectedItems: Set<string>;
  onClose: () => void;
  onConfirm: () => void;
  onSelectedItemsChange: (items: Set<string>) => void;
}

const ReturnItemSelectionModal: React.FC<ReturnItemSelectionModalProps> = ({
  isOpen,
  invoiceNo,
  customerName,
  items,
  selectedItems,
  onClose,
  onConfirm,
  onSelectedItemsChange,
}) => {
  if (!isOpen || items.length === 0) {
    return null;
  }

  const selectableItems = items.filter((item) => item.lineType !== 'RETURN');

  return (
    <div className="search-modal-overlay">
      <div className="search-modal" style={{ maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
        <div className="search-modal-header" style={{ paddingBottom: '12px' }}>
          <div>
            <Text weight="strong" size="large">
              Select Items to Return
            </Text>
            <div style={{ marginTop: '4px' }}>
              <Text tone="secondary" size="small">
                Invoice: {invoiceNo} | Customer: {customerName || 'Walk-in'}
              </Text>
            </div>
          </div>
          <button type="button" onClick={onClose} className="search-modal-close">
            ✕
          </button>
        </div>

        <div className="search-modal-content" style={{ padding: '14px 16px 16px' }}>
          <Stack space="medium">
            <Box display="flex" alignItems="center" gap="small" style={{ padding: '2px 4px 0' }}>
              <input
                type="checkbox"
                checked={selectableItems.length > 0 && selectableItems.every((item) => selectedItems.has(item.itemCode || item.id))}
                onChange={(event) => {
                  const selectableCodes = selectableItems.map((item) => item.itemCode || item.id);
                  onSelectedItemsChange(event.target.checked ? new Set(selectableCodes) : new Set());
                }}
                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
              />
              <Text weight="strong" size="small">
                Select all returnable items
              </Text>
            </Box>

            <div style={{ maxHeight: '330px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '6px', padding: '8px 10px', backgroundColor: '#fafafa' }}>
              {items.map((item) => {
                const itemKey = item.itemCode || item.id;
                const isAlreadyReturned = item.lineType === 'RETURN';
                const isSelected = selectedItems.has(itemKey);

                return (
                  <Box
                    key={itemKey}
                    display="flex"
                    alignItems="center"
                    justifyContent="spaceBetween"
                    paddingY="small"
                    style={{
                      borderBottom: '1px solid #f0f0f0',
                      paddingLeft: '6px',
                      paddingRight: '6px',
                    }}
                  >
                    <Box display="flex" alignItems="flexStart" gap="small">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isAlreadyReturned}
                        onChange={(event) => {
                          const nextSelected = new Set(selectedItems);
                          if (event.target.checked) {
                            nextSelected.add(itemKey);
                          } else {
                            nextSelected.delete(itemKey);
                          }
                          onSelectedItemsChange(nextSelected);
                        }}
                        style={{
                          cursor: isAlreadyReturned ? 'not-allowed' : 'pointer',
                          width: '18px',
                          height: '18px',
                          opacity: isAlreadyReturned ? 0.45 : 1,
                        }}
                      />
                      <Box style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Text weight="strong" size="small">
                          {item.name}
                        </Text>
                        <Text tone="secondary" size="xsmall">
                          Qty: {item.quantity} | Price: {item.price.toFixed(2)}
                        </Text>
                        {isAlreadyReturned && (
                          <div className="return-badge" style={{ marginTop: '2px', display: 'inline-flex' }}>
                            Already returned
                          </div>
                        )}
                      </Box>
                    </Box>
                    <Text weight="strong" size="small">
                      {item.total.toFixed(2)}
                    </Text>
                  </Box>
                );
              })}
            </div>

            <Box
              display="flex"
              justifyContent="spaceBetween"
              paddingY="small"
              paddingX="small"
              style={{
                borderTop: '2px solid #e0e0e0',
                borderBottom: '2px solid #e0e0e0',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
                marginTop: '4px',
                marginBottom: '4px',
              }}
            >
              <Text weight="strong">Items Selected:</Text>
              <Text weight="strong" size="large">
                {selectedItems.size} / {selectableItems.length}
              </Text>
            </Box>

            <Box display="flex" gap="medium" marginTop="small">
              <button
                type="button"
                className="billing-bottom-btn clear"
                onClick={onClose}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="billing-bottom-btn charge"
                onClick={onConfirm}
                disabled={selectedItems.size === 0}
                style={{
                  flex: 1,
                  opacity: selectedItems.size === 0 ? 0.5 : 1,
                  cursor: selectedItems.size === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                Confirm Selection ({selectedItems.size})
              </button>
            </Box>
          </Stack>
        </div>
      </div>
    </div>
  );
};

export default ReturnItemSelectionModal;
